import React from "react"

import { Button, Grid, Icon, Typography } from "@material-ui/core"
import useDidMount from "@/hooks/useDidMount"
import AnalyticsService from "@/services/Analytics"
import { CheckBox } from "@material-ui/icons"

import Breadcrumb from "@/components/BreadcrumbNew"

import useStyles from "@/pages/Admin/Playground/styles"
import { themeColors } from "letalkui"

import ContactSupportSVGIcon from "@/assets/icons/contact_support_icon.svg"
import AIHumanComputerSVGIcon from "@/assets/images/illustrations/AI_human_computer.svg"
import AIHuggingSVGIcon from "@/assets/images/illustrations/AI_hugging.svg"

import PortletWithNewsContent from "@/components/PortletWithNewsContent"
import FeatureStatusChip from "@/components/FeatureStatusChip"
import { formsLinks } from "@/utils/link"

const Playground: React.FC = () => {
	const classes = useStyles()

	useDidMount(() => { AnalyticsService.capture("ai_playground_opened") })

	const QUESTIONS: string[] = [
		"“Quais são as dúvidas mais comuns dos meus clientes?”",
		"“Por que perdemos esse lead?”",
		"“Como o atendente João costuma responder sobre o procedimento X?”",
		"“Quais clientes reclamaram nos últimos 7 dias?”",
		"“Quais foram os contatos que perguntaram sobre parcelamento?”"
	]

	const BUSINESS_TARGETS: Array<{
		title: string
		content: string
	}> = [
		{
			title: "Gestores de Atendimento e Customer Experience",
			content: "que desejam acompanhar a qualidade do atendimento, identificar falhas recorrentes, ajustar scripts e treinar a equipe com base em dados reais."
		},
		{
			title: "Empresas com alto volume de conversas via WhatsApp",
			content: "que precisam transformar milhares de mensagens em insights práticos, sem depender de análises manuais."
		},
		{
			title: "Líderes Comerciais e de Pré-vendas (SDR/BDR)",
			content: "que querem entender por que alguns leads não avançaram, quais objeções são mais comuns, e como otimizar o processo de qualificação."
		},
		{
			title: "Equipes de Produto e Marketing",
			content: "que buscam feedback direto do cliente sobre o que está funcionando (ou não), para embasar melhorias e campanhas mais alinhadas às dores reais do público."
		},
		{
			title: "Empresas com múltiplos atendentes ou unidades",
			content: "que precisam comparar abordagens, padronizar comunicações e garantir consistência em toda a operação."
		}
	]

	return (
		<Grid
			container
			direction="column"
			spacing={6}
		>
			<Grid item>
				<Grid container direction="column" spacing={3}>
					<Grid item>
						<Breadcrumb
							data={[
								{ name: "Playground", pathname: "/admin/playground" }
							]}
						/>
					</Grid>

					<Grid item>
						<Grid container direction="column">
							<Grid item>
								<FeatureStatusChip status="upcoming"/>
							</Grid>

							<Grid item className={classes.portlet}>
								<PortletWithNewsContent
									body={{}}
									title={{
										value: "Quer ser um dos primeiros a testar?"
									}}
								>
									<Grid
										container
										justifyContent="center"
										direction="column"
									>
										<Typography
											variant="h1"
											className={classes.bodyText}
										>
											Seja <strong>Beta Tester</strong> e descubra o poder de conversar com os dados da sua operação.
										</Typography>

										<Typography
											variant="h1"
											className={classes.bodyAuxiliarText}
										>
											É rápido, <strong>menos de 1 minuto</strong>.
										</Typography>

										<Button
											className={classes.button}
											variant="contained"
											href={formsLinks.playgroundInitialForm}
											target="_blank"
										>
											Quero ser Beta Tester
										</Button>
									</Grid>
								</PortletWithNewsContent>
							</Grid>

							<Grid item>
								<Grid container direction="row">
									<Grid item xs={6}>
										<Grid container>
											<Grid item>
												<Typography
													className={classes.calloutTitle}
												>
													Playground Inteligente com Histórico de Conversas
												</Typography>
											</Grid>

											<Grid item>
												<Typography
												>
													Imagine um <strong>ChatGPT conectado ao histórico completo de atendimento da sua conta</strong> no WhatsApp — é exatamente isso que o Playground oferece. Trata-se de uma funcionalidade onde você pode interagir com uma inteligência artificial treinada especificamente com os dados da sua operação.
													<br />
													<br />
													No Playground, você pode <strong>fazer perguntas em linguagem natural</strong> como:

													{
														QUESTIONS.map((QUESTION, index) => {
															return (
																<Grid
																	item
																	xs={12}
																	key={index}
																>
																	<Grid
																		container
																		spacing={1}
																		className={classes.listedItem}
																	>
																		<Grid
																			item
																		>
																			<Grid
																				container
																				alignItems="flex-start"
																			>
																				<Icon>
																					<img
																						src={ContactSupportSVGIcon}
																						style={{
																							width: 24,
																							height: 24
																						}}
																						alt="ia-agent-icon"
																					/>
																				</Icon>
																			</Grid>
																		</Grid>
																		<Grid
																			item
																			xs={10}
																		>
																			<Typography
																			>
																				{QUESTION}
																			</Typography>
																		</Grid>
																	</Grid>
																</Grid>
															)
														})
													}
													<br />
													A IA analisa o contexto completo das conversas, identificando <strong>padrões e insights</strong> relevantes para transformar seu <strong>histórico em uma base viva</strong> de conhecimento, permitindo melhorias nos processos, treinamentos mais eficientes e uma compreensão mais profunda da jornada do cliente.
													<br />
													<br />
												</Typography>
											</Grid>
										</Grid>
									</Grid>

									<Grid item xs={6}>
										<img
											src={AIHumanComputerSVGIcon}
											className={classes.calloutImage}
										/>
									</Grid>

									<Grid item xs={6}>
										<img
											src={AIHuggingSVGIcon}
											className={classes.calloutImage}
										/>
									</Grid>

									<Grid item xs={6}>
										<Typography
										>
											<Grid item>
												<Typography
													className={classes.calloutTitle}
												>
													Pra quem é essa solução?
												</Typography>
											</Grid>

											{
												BUSINESS_TARGETS.map((BUSINESS_TARGET, index) => {
													return (
														<Grid
															item
															xs={12}
															key={index}
														>
															<Grid
																container
																spacing={1}
																className={classes.listedItem}
															>
																<Grid
																	item
																>
																	<Grid
																		container
																		alignItems="flex-start"
																	>
																		<CheckBox
																			style={{
																				width: 24,
																				height: 24,
																				fill: themeColors.colors.system.green[500]
																			}}
																		/>
																	</Grid>
																</Grid>
																<Grid
																	item
																	xs={10}
																>
																	<Typography
																	>
																		<strong>{BUSINESS_TARGET.title}</strong> {BUSINESS_TARGET.content}
																	</Typography>
																</Grid>
															</Grid>
														</Grid>
													)
												})
											}
										</Typography>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	)
}

export default Playground
